/**
 * Birdi Tracing API
 * Based on OpenTelemetry protocol
 * Basically it's similar but we take a log first approach & never glue our spans together
 * Forensics over APM for now but with a path towards standardised APM via OT
 */
export const FORMAT_HTTP_HEADERS = 'FORMAT_HTTP_HEADERS';
export const FORMAT_TEXT_MAP = 'FORMAT_TEXT_MAP';
const well_known = {
    'span.kind': 'String',
    'http.status_code': 'Number',
    'error': 'Boolean',
    'http.method': 'String',
    'http.url': 'String',
    'http.final_path': 'String',
    'sampling.priority': 'Number',
    'birdi.context': 'String',
    'app_version': 'String'
};
export class Tracer {
    constructor(opts) {
        // Validation
        if (!opts.type) {
            throw new Error('tracer opts must have a type');
        }
        if (!opts.logger) {
            throw new Error('tracer opts must have hexrandom');
        }
        if (!opts.hexrandom) {
            throw new Error('tracer opts must have hexrandom function');
        }
        if (!opts.timestamper) {
            throw new Error('tracer opts must have timestamper function');
        }
        this.type = opts.type;
        this.logger = opts.logger;
        this.hexrandom = opts.hexrandom;
        this.timestamper = opts.timestamper;
        this.process_id = this.hexrandom(8);
        this.logger.info(`@birdi/tracing init ${this.type} ts=${this.timestamper()} pid=${this.process_id}`);
    }
    end() {
        this.logger.info(`@birdi/tracing fini ${this.type} ts=${this.timestamper()} pid=${this.process_id}`);
        if (this.type === 'cli')
            this.logger.end && this.logger.end();
    }
    start_span(name, args = {}) {
        const parent_span = args.childOf?.context || args.followsFrom?.context ||
            {
                trace_id: this.hexrandom(8),
                span_id: '0'
            };
        const span_id = this.hexrandom(8);
        const span_ref_kind = args.followsFrom ? 'FollowsFrom' : 'ChildOf';
        const trace_id = parent_span.trace_id;
        const parent_span_id = parent_span.span_id;
        const tags = convert_tags(args.tags);
        const events = [];
        const start_time = this.timestamper();
        const finish_time = start_time;
        const process_id = this.process_id;
        const context = {
            start_time,
            finish_time,
            name,
            process_id,
            trace_id,
            span_id,
            parent_span_id,
            span_ref_kind,
            tags,
            events,
        };
        // TODO: What does span_emit do?
        this.span_emit(context.start_time, 'span_start', context, { span: context });
        return new Span(this, context);
    }
    span_finish(span) {
        span.finish_time = this.timestamper();
        this.span_emit(span.finish_time, 'span_finish', span, { span });
    }
    // Logs something, shorthand for span_emit
    span_log_info(span, msg) {
        msg = to_string(msg);
        this.span_emit(this.timestamper(), 'span_info', span, { msg });
    }
    // Logs something
    span_emit(timestamp, span_msg_type, span, custom) {
        this.logger.info({
            birditracing: 2,
            timestamp,
            span_msg_type,
            process_id: span.process_id,
            trace_id: span.trace_id,
            span_id: span.span_id,
            ...custom,
        });
    }
    // Logs an error
    span_error(timestamp, span, message, stack) {
        this.logger.error({
            birditracing: 2,
            timestamp,
            span_msg_type: 'span_error',
            process_id: span.process_id,
            trace_id: span.trace_id,
            span_id: span.span_id,
            stack,
            message,
        });
    }
    /**
     * Adds event to log output
     */
    span_append_event(span, log_event) {
        span.events.push({
            timestamp: this.timestamper(),
            log_event: log_event,
        });
    }
    span_set_tag(span, key, value) {
        [key, value] = convert_tag(key, value);
        span.tags[key] = value;
    }
    span_set_operation_name(span, name) {
        span.name = to_string(name);
    }
    // get a span off the wire.
    span_extract(format, data) {
        // perhaps this should just be done in start_span.
        // that would make this a bit more opaque.
        const name = 'extracted';
        if (format === FORMAT_HTTP_HEADERS) {
            let trace_id = data['x-b3-traceid'];
            let parent_span_id = data['x-b3-parentspanid'];
            let span_id = data['x-b3-spanid'];
            // must have at least trace_id and span_id.
            // they can't be zero anyway.
            if (trace_id && span_id) {
                return new Span(this, { name, trace_id, span_id, parent_span_id });
            }
        }
        else if (format === FORMAT_TEXT_MAP) {
            if (data['uber-trace-id']) {
                let [trace_id, parent_span_id, span_id] = data['uber-trace-id'].split(':');
                if (trace_id && span_id)
                    return new Span(this, { name, trace_id, span_id, parent_span_id });
            }
        }
        else {
            // TODO - make this a logged soft error.
            throw new Error('unknown span format extract ' + format);
        }
        return new Span(this, { name: 'failed_extraction' });
    }
    // put a span onto the wire
    span_inject(span, format, target) {
        // TODO - save the span in http format
        if (format === FORMAT_HTTP_HEADERS) {
            target["x-b3-traceid"] = span.context.trace_id;
            target["x-b3-parentspanid"] = span.context.parent_span_id;
            target["x-b3-spanid"] = span.context.span_id;
        }
        else if (format === FORMAT_TEXT_MAP) {
            target["uber-trace-id"] = [span.context.trace_id, span.context.span_id, span.context.parent_span_id, "1"].join(":");
        }
        else {
            // TODO - make this a logged soft error.
            throw new Error('could not inject span with format ' + format);
        }
    }
}
// undefined is allowed here and will pass through
function to_string(o) {
    if (typeof o === 'string' || o instanceof String) {
        return o;
    }
    return JSON.stringify(o);
}
function convert_tag(key, value) {
    key = to_string(key);
    if (well_known[key] == 'Number') {
        value = Number(value) || 0;
    }
    else if (well_known[key] == 'String') {
        value = to_string(value) || '';
    }
    else if (well_known[key] == 'Boolean') {
        value = Boolean(value) || false;
    }
    else {
        value = to_string(value) || '';
        key += '.unknown';
    }
    return [key, value];
}
function convert_tags(t) {
    let h = {};
    if (t) {
        for (const [key, value] of Object.entries(t)) {
            let k, v;
            [k, v] = convert_tag(key, value);
            h[k] = v;
        }
    }
    return h;
}
/**
 * The raw span (i.e. the stuff that goes on the wire) is contained with Span.context for convenience
 */
export class Span {
    constructor(tracer, context) {
        this.tracer = tracer;
        this.context = {
            // defaults
            tags: [],
            events: [],
            ...context,
        };
    }
    finish() {
        this.tracer.span_finish(this.context);
    }
    logEvent(eventName, payload) {
        this.tracer.span_append_event(this.context, {
            'event': eventName, ...payload && { 'message': to_string(payload) }
        });
    }
    logInfo(msg) {
        this.tracer.span_log_info(this.context, msg);
    }
    logError(err) {
        const attributes = { event: 'error' };
        if (err instanceof Error) {
            attributes.message = err.message;
        }
        else {
            attributes.message = to_string(err);
        }
        this.tracer.span_append_event(this.context, attributes);
        this.tracer.span_error(this.tracer.timestamper(), this.context, attributes.message, err?.stack);
    }
    setTag(key, value) {
        this.tracer.span_set_tag(this.context, key, value);
    }
    setOperationName(name) {
        this.tracer.span_set_operation_name(this.context, name);
    }
    /**
     * Adds error event to span.
     * NB Does not immediately log out to stdout
     * But will log on finish
     */
    catch(err) {
        this.setTag('error', true);
        this.setTag('sampling.priority', 1);
        this.logError(err);
    }
}
/**
 * @param span
 * @returns returns object containing individual trace, parentspan and span headers
 */
export function getHeaders(span) {
    return {
        'x-b3-traceid': span.trace_id,
        'x-b3-parentspanid': span.parent_span_id,
        'x-b3-spanid': span.span_id,
    };
}
/**
 *
 * @param span
 * @returns returns uber-trace-id header object e.g.
 * { 'uber-trace-id': 'trace_id:span_id:parent_span_id:1' }
 */
export function getCompactHeaders(span) {
    return {
        'uber-trace-id': [span.trace_id, span.span_id, span.parent_span_id, '1'].join(':'),
    };
}
